<template>
  <div class="edge-props">
    <div class="props-header">
      <span>连线属性</span>
      <button class="props-close" @click="$emit('close')" title="关闭">&times;</button>
    </div>
    <div class="props-body" v-if="edge">
      <!-- 基本信息 -->
      <details class="prop-group" open>
        <summary class="group-title"><i class="fas fa-info-circle"></i> 基本信息</summary>
        <div class="prop-row"><label>步骤名</label><input v-model="edge.props.showName" @input="save" class="prop-input" placeholder="内部步骤标识名" /></div>
        <div class="prop-row"><label>显示名</label><input v-model="edge.props.displayName" @input="save" class="prop-input" :placeholder="edge.props.showName || '画布上显示的名称'" /></div>
      </details>

      <!-- 处理确认（非条件节点的源节点） -->
      <details class="prop-group" v-if="!isFromCondition" open>
        <summary class="group-title"><i class="fas fa-check-circle"></i> 处理确认</summary>
        <div class="prop-row"><label>需要批注</label>
          <input type="checkbox" v-model="edge.props.needRemark" @change="save" class="prop-check" />
        </div>
      </details>

      <!-- 条件分支（仅条件节点源） -->
      <details class="prop-group" v-if="isFromCondition" open>
        <summary class="group-title"><i class="fas fa-code-branch"></i> 条件分支</summary>
        <div class="prop-row"><label>对应条件</label>
          <select v-model="edge.props.conditionBranch" @change="onBranchChange" class="prop-input">
            <option value="1">条件成立</option>
            <option value="0">条件不成立</option>
          </select>
        </div>
      </details>

      <!-- 表单数据条件（所有连线） -->
      <details class="prop-group">
        <summary class="group-title"><i class="fas fa-filter"></i> 表单数据条件</summary>
        <div class="prop-row"><label>条件类型</label>
          <select v-model="edge.props.linkCondition" @change="save" class="prop-input">
            <option value="0">无条件</option>
            <option value="1">有条件</option>
          </select>
        </div>
        <template v-if="edge.props.linkCondition == 1">
          <div class="prop-row full"><label>条件表达式</label>
            <textarea v-model="edge.props.linkConditionCode" @input="save" class="prop-input code-input" rows="4" placeholder="输入条件表达式，如：Field1 > 100&#10;支持 &gt;、&gt;=、=、&lt;、&lt;=、&lt;&gt;" spellcheck="false"></textarea>
          </div>
          <!-- 条件生成器 -->
          <div class="cond-builder">
            <div class="cond-builder-row">
              <select v-model="condBuilder.field" class="prop-input" style="width:110px">
                <option value="">-- 选择字段 --</option>
                <optgroup v-for="tg in tableGroups" :key="tg.name" :label="tg.name || '主表'">
                  <option v-for="col in tg.columns" :key="col.columnName" :value="col.columnName">{{ col.columnName }}</option>
                </optgroup>
                <optgroup label="系统变量">
                  <option value="CreateTime">创建时间</option>
                  <option value="CreateUser">创建人</option>
                  <option value="SerialNum">流水号</option>
                  <option value="SummaryInfo">摘要信息</option>
                </optgroup>
              </select>
              <select v-model="condBuilder.op" class="prop-input" style="width:60px">
                <option value=">"> &gt; </option>
                <option value=">="> &gt;= </option>
                <option value="=" selected>=</option>
                <option value="<"> &lt; </option>
                <option value="<="> &lt;= </option>
                <option value="<>"> &lt;&gt; </option>
              </select>
              <input v-model="condBuilder.value" placeholder="值" class="prop-input" style="width:100px" @keyup.enter="appendCondition" />
              <button class="btn-cond-add" @click="appendCondition" title="添加条件"><i class="fas fa-plus"></i></button>
            </div>
            <div class="cond-builder-hint">选择字段和操作符，输入值后点击 + 或回车添加条件</div>
          </div>
        </template>
      </details>

      <!-- 删除 -->
      <div class="prop-actions">
        <button class="btn-delete" @click="$emit('delete', edge.id)"><i class="fas fa-trash"></i> 删除此连线</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive } from 'vue'

const props = defineProps({
  edge: { type: Object, default: null },
  nodes: { type: Array, default: () => [] },
  tableGroups: { type: Array, default: () => [] }
})
const emit = defineEmits(['update', 'delete', 'close'])

function save() { emit('update') }

/** 条件构建器状态 */
const condBuilder = reactive({ field: '', op: '=', value: '' })

/** 添加条件表达式到 linkConditionCode */
function appendCondition() {
  if (!condBuilder.field || !condBuilder.value) return
  const expr = `${condBuilder.field} ${condBuilder.op} ${condBuilder.value}`
  if (props.edge?.props) {
    const current = props.edge.props.linkConditionCode || ''
    props.edge.props.linkConditionCode = current ? current + '\n' + expr : expr
    save()
  }
  condBuilder.field = ''; condBuilder.value = ''
}

/** 条件分支切换时触发保存并更新连线标签 */
function onBranchChange() {
  save()
}

/** 判断源节点是否为条件节点 (type=3) */
const isFromCondition = computed(() => {
  if (!props.edge || !props.nodes) return false
  const src = props.nodes.find(n => n.id === props.edge.from)
  return src && src.nodeType === 3
})
</script>

<style scoped>
.edge-props { width: 290px; background: #fff; display: flex; flex-direction: column; flex-shrink: 0; border-left: 1px solid #e5e7eb; }
.props-header { padding: 8px 12px; font-size: 12px; font-weight: 600; color: #374151; background: #f5f6f8; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
.props-close { background: none; border: none; color: #9ca3af; font-size: 16px; cursor: pointer; padding: 0 2px; line-height: 1; }
.props-close:hover { color: #374151; }
.props-body { flex: 1; overflow-y: auto; }

.prop-group { border-bottom: 1px solid #f3f4f6; }
.prop-group[open] .group-title { background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
.group-title { padding: 8px 12px; font-size: 11px; font-weight: 600; color: #374151; cursor: pointer; display: flex; align-items: center; gap: 6px; user-select: none; list-style: none; transition: background 0.1s; }
.group-title:hover { background: #f9fafb; }
.group-title::-webkit-details-marker { display: none; }
.group-title i { font-size: 10px; width: 14px; text-align: center; color: #9ca3af; }

.prop-row { display: flex; align-items: center; gap: 6px; padding: 5px 12px; }
.prop-row.full { flex-direction: column; align-items: stretch; }
.prop-row label:first-child { width: 72px; font-size: 11px; color: #6b7280; text-align: right; flex-shrink: 0; }
.prop-input { flex: 1; border: 1px solid #e5e7eb; border-radius: 4px; padding: 4px 8px; font-size: 11px; outline: none; min-width: 0; transition: border-color 0.15s; background: #fff; color: #1f2937; }
.prop-input:focus { border-color: #10b981; box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1); }
textarea.prop-input { resize: vertical; font-family: inherit; }
select.prop-input { height: 26px; cursor: pointer; }
.prop-check { width: 16px; height: 16px; cursor: pointer; accent-color: #10b981; }

.code-input { font-family: 'Cascadia Code', Consolas, monospace; background: #f8fafc; }

.cond-builder { padding: 4px 12px 8px; }
.cond-builder-row { display: flex; gap: 4px; align-items: center; }
.cond-builder-row .prop-input { flex: none; }
.cond-builder-hint { font-size: 10px; color: #9ca3af; margin-top: 4px; }
.btn-cond-add { width: 26px; height: 26px; border: 1px solid #d1d5db; border-radius: 4px; background: #f9fafb; color: #10b981; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 11px; }
.btn-cond-add:hover { background: #ecfdf5; border-color: #10b981; }

.prop-actions { padding: 10px 12px; border-top: 1px solid #e5e7eb; }
.btn-delete { padding: 6px 0; border: 1px solid #fee2e2; border-radius: 4px; background: #fff; font-size: 11px; color: #f87171; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 4px; width: 100%; transition: all 0.15s; }
.btn-delete:hover { background: #fef2f2; color: #ef4444; border-color: #ef4444; }
</style>
